import { useState, useMemo } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { matchSorter } from "match-sorter";
import {
  Button as SpendeskButton,
  Icon as SpendeskIcon,
} from "@dev-spendesk/grapes";

import LoginButton from "./LoginButton";
import { DeleteModal } from "./Components/DeleteModal";
import { StatusBadge } from "./Components/StatusBadge";
import { Spinner } from "./Components/Spinner";
import { BranchChip, BranchesFold } from "./Components/BranchTree";
import { buildCompanyTree } from "./Components/buildCompanyTree";
import "./Components/BranchTree.css";

const CompanyCard = ({
  item,
  onProcessSelection,
  deleteProcess,
  isBranch = false,
  branchCount = 0,
  isExpanded = false,
  onToggleBranches,
  parentName,
  isDimmed = false,
}) => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);

  const handleCardClick = (e) => {
    if (e.target.closest("button")) return;
    onProcessSelection(item.id);
  };

  return (
    <>
      <div
        onClick={handleCardClick}
        style={{
          border: isHovered ? "1px solid #c4b5fd" : "1px solid #e5e7eb",
          backgroundColor: isBranch ? "#fafafb" : "white",
          borderRadius: "12px",
          padding: isBranch ? "15px 18px" : "20px",
          opacity: isDimmed ? 0.45 : 1,
          boxShadow: isHovered ? "0 10px 15px -3px rgba(0, 0, 0, 0.1)" : "none",
          transition: "all 0.2s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Left: Company Info */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "8px",
            }}
          >
            <h3
              style={{
                fontSize: isBranch ? "16px" : "18px",
                fontWeight: "600",
                color: isHovered ? "#7c3aed" : "#111827",
                margin: 0,
                transition: "color 0.2s ease",
              }}
            >
              {item.company_name}
            </h3>
            <StatusBadge status={item.status} />
            {!isBranch && parentName && (
              <BranchChip
                label={`Branch of ${parentName}`}
                title="Its parent is not in this list"
              />
            )}
            {branchCount > 0 && (
              <BranchChip
                label={`${branchCount} ${
                  branchCount > 1 ? "branches" : "branch"
                }`}
                onClick={onToggleBranches}
                expanded={isExpanded}
              />
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              fontSize: "14px",
              color: "#6b7280",
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontWeight: "300",
                fontSize: "12px",
              }}
            >
              <SpendeskIcon color="currentColor" name="pen" size="s" />
              {item.created_by}
            </span>
            {item.company_id && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontWeight: "300",
                  fontSize: "12px",
                }}
              >
                <SpendeskIcon color="currentColor" name="badge" size="s" />
                {item.company_id}
              </span>
            )}
            {item.billing_subscription_plan && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontWeight: "300",
                  fontSize: "12px",
                }}
              >
                <SpendeskIcon color="currentColor" name="settings" size="s" />
                {item.billing_subscription_plan}
              </span>
            )}
            {item.banking_provider && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontWeight: "300",
                  fontSize: "12px",
                }}
              >
                <SpendeskIcon color="currentColor" name="bank" size="s" />
                {item.banking_provider === "bankable" ? "Bankable" : "SFS"}
              </span>
            )}
          </div>
        </div>

        {/* Right: Actions */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <LoginButton processId={item.id} text="Log as AO" />

          <button
            onClick={() => setIsDeleteModalVisible(true)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
              backgroundColor: isDeleteHovered ? "#fef2f2" : "white",
              border: isDeleteHovered
                ? "1px solid #fca5a5"
                : "1px solid #d1d5db",
              borderRadius: "8px",
              color: isDeleteHovered ? "#ef4444" : "#9ca3af",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={() => setIsDeleteHovered(true)}
            onMouseLeave={() => setIsDeleteHovered(false)}
          >
            <SpendeskIcon color="currentColor" name="trash" size="s" />
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isDeleteModalOpen={isDeleteModalVisible}
        setIsDeleteModalOpen={setIsDeleteModalVisible}
        handleRemoveProcess={deleteProcess}
        processId={item.id}
      />
    </>
  );
};

export default function ListProcess(props) {
  const {
    processes,
    userEmail,
    onProcessSelection,
    onCreateCompanyClick,
    deleteProcess,
    isLoading,
  } = props;

  const [isPersonalViewSelected, setPersonalViewSelected] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [expandedIds, setExpandedIds] = useState({});

  const processSelected =
    isPersonalViewSelected && processes.length
      ? processes.filter((process) => process.created_by === userEmail)
      : processes;

  const list = useMemo(() => {
    return matchSorter(processSelected, searchInput, {
      keys: ["company_name", "created_by"],
    });
  }, [processSelected, searchInput]);

  const isSearching = searchInput.trim().length > 0;
  const tree = useMemo(
    () =>
      buildCompanyTree({
        matches: list,
        visible: processSelected,
        all: processes,
        isSearching,
      }),
    [list, processSelected, processes, isSearching]
  );

  const toggleBranches = (id) =>
    setExpandedIds((current) => ({ ...current, [id]: !current[id] }));

  return (
    <div
      style={{
        maxWidth: "1024px",
        margin: "0 auto",
        padding: "32px 16px",
      }}
    >
      {/* Title */}
      <h1
        style={{
          fontSize: "30px",
          fontWeight: "700",
          textAlign: "center",
          marginBottom: "40px",
        }}
      >
        Make a demo
      </h1>

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {/* Search Input */}
          <div style={{ position: "relative" }}>
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "12px",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                color: "#9ca3af",
              }}
            >
              <SpendeskIcon color="currentColor" name="search" size="s" />
            </div>
            <input
              type="text"
              placeholder="Search by company name or email"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              style={{
                width: "320px",
                paddingLeft: "40px",
                paddingRight: "16px",
                paddingTop: "12px",
                paddingBottom: "12px",
                backgroundColor: "white",
                border: isSearchFocused
                  ? "2px solid #7c3aed"
                  : "1px solid #d1d5db",
                borderRadius: "12px",
                fontSize: "14px",
                outline: "none",
                transition: "all 0.2s ease",
              }}
            />
          </div>

          {/* Create Button */}
          <SpendeskButton
            fit="content"
            iconName="plus"
            iconPosition="left"
            text="Create a company"
            onClick={() => onCreateCompanyClick(true)}
            type="button"
            variant="primary"
            style={{ borderRadius: "8px", padding: "5px 8px" }}
          />
        </div>

        {/* Filter Toggle */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "4px",
          }}
        >
          <button
            onClick={() => setPersonalViewSelected(true)}
            style={{
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "500",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              transition: "all 0.2s ease",
              backgroundColor: isPersonalViewSelected
                ? "#5d21d2"
                : "transparent",
              color: isPersonalViewSelected ? "white" : "#4b5563",
            }}
          >
            My companies
          </button>
          <button
            onClick={() => setPersonalViewSelected(false)}
            style={{
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "500",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              transition: "all 0.2s ease",
              backgroundColor: !isPersonalViewSelected
                ? "#7c3aed"
                : "transparent",
              color: !isPersonalViewSelected ? "white" : "#4b5563",
            }}
          >
            All
          </button>
        </div>
      </div>

      {/* Companies List */}
      <div
        style={{
          backgroundColor: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "16px",
          overflow: "hidden",
          height: "calc(100vh - 320px)",
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        <div style={{ padding: "16px" }}>
          {isLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "64px 0",
              }}
            >
              <Spinner />
            </div>
          ) : tree.length === 0 ? (
            <div style={{ textAlign: "center", padding: "64px 0" }}>
              <p style={{ color: "#6b7280", fontSize: "18px", margin: 0 }}>
                No companies found
              </p>
              <p
                style={{
                  color: "#9ca3af",
                  fontSize: "14px",
                  marginTop: "4px",
                }}
              >
                Try adjusting your search or create a new company
              </p>
            </div>
          ) : (
            <InfiniteScroll initialLoad={false} pageStart={0} useWindow={false}>
              {tree.map(({ item, branches, parentName, isDimmed }) => {
                const isExpanded = isSearching || Boolean(expandedIds[item.id]);
                return (
                  <div className="pl-group" key={item.id}>
                    <CompanyCard
                      item={item}
                      onProcessSelection={onProcessSelection}
                      deleteProcess={deleteProcess}
                      branchCount={branches.length}
                      isExpanded={isExpanded}
                      onToggleBranches={() => toggleBranches(item.id)}
                      parentName={parentName}
                      isDimmed={isDimmed}
                    />
                    {branches.length > 0 && (
                      <BranchesFold open={isExpanded}>
                        {branches.map((branch) => (
                          <div className="pl-branch" key={branch.item.id}>
                            <CompanyCard
                              item={branch.item}
                              isBranch
                              onProcessSelection={onProcessSelection}
                              deleteProcess={deleteProcess}
                              isDimmed={branch.isDimmed}
                            />
                          </div>
                        ))}
                      </BranchesFold>
                    )}
                  </div>
                );
              })}
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
}
